import useLocalStorage from '@/modules/common/hooks/use-local-storage.hook'
import {
  PaginationState,
  RowSelectionState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table'
import { useCallback, useEffect, useState } from 'react'

/**
 * Generic hook for managing TanStack Table state in a DRY and reusable way.
 *
 * It abstracts table UI state such as pagination, sorting, filtering, column visibility,
 * and row selection — while allowing data fetching logic to be passed in externally.
 *
 * @template T - Type of table records. Must include a `uid` field for unique identification.
 * @template V - Type of filter object. Must be an object (Record) representing applied filters.
 * keyPrefix - A unique prefix used for localStorage keys (to persist table state per table instance).
 */
interface UseTanstackTableProps<
  T extends { uid: string },
  V extends Record<string, any>,
> {
  keyPrefix: string

  queryFn: (params: {
    pagination: PaginationState
    sorting: SortingState
    filters: V
  }) => {
    data?: { results: T[]; total: number }
    isLoading: boolean
    isError: boolean
  }
  defaultFilters?: V
  defaultSortingState?: SortingState
  defaultColumnVisibility?: VisibilityState
}

export const useTanstackTable = <
  T extends { uid: string },
  V extends Record<string, any>,
>({
  keyPrefix,
  queryFn,
  defaultFilters,
  defaultSortingState,
  defaultColumnVisibility
}: UseTanstackTableProps<T, V>) => {
  // --- State ---
  const [filters, setFilters] = useState<V>(defaultFilters ?? ({} as V))
  const [appliedFilters, setAppliedFilters] = useState<V>(
    defaultFilters ?? ({} as V),
  )
  const [selectedRows, setSelectedRows] = useState<T[]>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  // --- Local Storage states ---
  const defaultPagination: PaginationState = { pageSize: 50, pageIndex: 0 }
  const [pagination, setPagination] = useLocalStorage<PaginationState>(
    `${keyPrefix}-pagination`,
    defaultPagination,
  )

  const [columnVisibility, setColumnVisibility] =
    useLocalStorage<VisibilityState>(
      `${keyPrefix}-column-visibility`,
      defaultColumnVisibility ?? {id: false},
    )

  const [sorting, setSorting] = useState<SortingState>(
    defaultSortingState ?? [{ id: 'id', desc: true }],
  )

  // --- Query call (external) ---
  const { data, isLoading, isError } = queryFn({
    pagination,
    sorting,
    filters: appliedFilters,
  })

  const rowCount = data?.total ?? 0

  // --- Filtering helpers ---
  const applyAndSearch = useCallback(
    (nextFilters: V) => {
      setFilters(nextFilters)
      setAppliedFilters(nextFilters)
      setPagination((prev) => ({ ...prev, pageIndex: 0 }))
    },
    [setPagination],
  )

  const handleSearch = () => applyAndSearch(filters)

  const handleDeleteFilter = (key: keyof V) => {
    const nextFilters = { ...filters }
    delete nextFilters[key]
    applyAndSearch(nextFilters)
  }

  const handleClearAllFilters = () => {
    setAppliedFilters({} as V)
    setFilters({} as V)
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
    setSorting([{ id: 'id', desc: true }])
  }

  const syncFiltering = () => setFilters(appliedFilters)

  // --- Sync selected rows ---
  useEffect(() => {
    const selected = data?.results?.filter((row) => rowSelection[row.uid]) || []
    setSelectedRows(selected)
  }, [data?.results, rowSelection])

  // --- Return unified API ---
  return {
    // table data
    data,
    rowCount,
    isLoading,
    isError,

    // pagination / sorting
    defaultPagination,
    pagination,
    setPagination,
    sorting,
    setSorting,

    // visibility
    defaultColumnVisibility,
    columnVisibility,
    setColumnVisibility,

    // selection
    rowSelection,
    setRowSelection,
    selectedRows,

    // filtering
    filters,
    setFilters,
    appliedFilters,
    handleSearch,
    handleDeleteFilter,
    handleClearAllFilters,
    syncFiltering,
  }
}
